import React, { useState, useEffect, forwardRef } from "react";
import TreeView from "@material-ui/lab/TreeView";
import TreeItem from "@material-ui/lab/TreeItem";
import {
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Button,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { useDispatch, useSelector } from "react-redux";
import awsRegions from "aws-regions";
import {
  resetGenerateYaml,
  checkStackStatusMethod,
  checkAccountActivityMethod,
  accountActivitySuccessMethod,
  accountActivityErrorMethod,
  clearOrganizationalUnits,
} from "../../store/multiAccountOnboard/multiAccountSlice";
import Slide from "@mui/material/Slide";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import EventInsightConfigFields from "./EventInsightConfigFields";
import EventInsightSection from "./EventInsightSection";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

const OrganizationalTreeSelector = forwardRef(
  (
    {
      organizationalUnits,
      organizations,
      masterAccountDetails,
      closeModal,
      onAccountsAdded,
      onAccountActivityStatusChange,
      onEventInsightModeChange,
    },
    ref
  ) => {
      console.log("🚀 ~ organizations:", organizations)
    console.log(
      "🚀 ~ OrganizationalTreeSelector ~ organizationalUnits:",
      organizationalUnits
    );
    const dispatch = useDispatch();

    // Single state for selected accounts - this will be used throughout
    const [selectedAccounts, setSelectedAccounts] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState("");
    const [isCheckingStatus, setIsCheckingStatus] = useState(false);
    const [pollingInterval, setPollingInterval] = useState(null);
    const [statusMessage, setStatusMessage] = useState("");
    const [isCheckingAccountStatus, setIsCheckingAccountStatus] =
      useState(false);
    const [hasCheckedAccountStatus, setHasCheckedAccountStatus] =
      useState(false);
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const [cloudFormationURL, setCloudFormationURL] = useState("");
    const [isYamlPreviewOpen, setIsYamlPreviewOpen] = useState(false);
    const [isLaunched, setIsLaunched] = useState(false);
    const [showEventInsightPrompt, setShowEventInsightPrompt] = useState(false);
    const [enableEventInsight, setEnableEventInsight] = useState(null);
    // Store originally selected accounts for Event Insight mode
    const [originallySelectedAccounts, setOriginallySelectedAccounts] = useState([]);

    const {
      isGeneratingYaml,
      generateYamlResponse,
      generateYamlError,
      isCheckingStackStatus,
      stackStatusSuccessResponse,
      stackStatusError,
      isCheckingAcocuntActivity,
      accountActivitySuccess: accountActivitySuccessResponse,
      accountActivityError,
    } = useSelector((state) => state.organizationalUnits);

    // Notify parent component when account activity status changes
    useEffect(() => {
      if (onAccountActivityStatusChange && accountActivitySuccessResponse) {
        onAccountActivityStatusChange(accountActivitySuccessResponse);
      }
    }, [accountActivitySuccessResponse, onAccountActivityStatusChange]);

    // Notify parent component when Event Insight mode changes
    useEffect(() => {
      if (onEventInsightModeChange) {
        onEventInsightModeChange(showEventInsightPrompt);
      }
    }, [showEventInsightPrompt, onEventInsightModeChange]);

    console.log("🚀 ~ accountActivityError:", accountActivityError);

    // Helper function to check if an account is selected
    const isAccountSelected = (accountId) => {
      return selectedAccounts.includes(accountId);
    };

    // Helper function to check if an account should be disabled in Event Insight mode
    const isAccountDisabledInEventInsight = (acc) => {
      if (!showEventInsightPrompt) return acc.onboarded;
      
      const isNotMaster = acc.accountId !== masterAccountDetails.accountNumber;
      
      // Debug logging
      console.log("🔍 Checking account:", acc.accountName, "accountId:", acc.accountId, "accountNumber:", acc.accountNumber);
      console.log("🔍 Organizations:", organizations?.map(org => ({ accountNumber: org.accountNumber, accountName: org.accountName })));
      
      // Check if account exists in the main organizations list
      // Try to match by accountId first, then by accountNumber
      const matchingOrgAccount = organizations?.find(org => 
        org.accountNumber === acc.accountId || 
        org.accountNumber === acc.accountNumber ||
        org.accountNumber === acc.accountId
      );
      
      console.log("🔍 Matching org account:", matchingOrgAccount);
      
      // Check if account was originally selected
      const wasOriginallySelected = originallySelectedAccounts.includes(acc.accountId);
      
      // If account exists in main list, check Event Insight status
      if (matchingOrgAccount) {
        const eventInsightAddon = matchingOrgAccount.subscription?.addons?.find(addon => addon.name === "Event Insight");
        const isEventInsightEnabled = eventInsightAddon?.enabled === true;
        
        // Account should be selectable if:
        // 1. It was originally selected, OR
        // 2. Event Insight addon is disabled (user can enable it)
        // 3. It's not the master account
        const shouldBeSelectable = (wasOriginallySelected || !isEventInsightEnabled) && isNotMaster;
        
        return !shouldBeSelectable;
      }
      
      // If account doesn't exist in main list, only allow if it was originally selected
      return !wasOriginallySelected || !isNotMaster;
    };

    // Helper function to get Event Insight status for an account
    const getEventInsightStatus = (accountId) => {
      const matchingOrgAccount = organizations?.find(org => 
        org.accountNumber === accountId
      );
      if (!matchingOrgAccount) return null;
      
      const eventInsightAddon = matchingOrgAccount.subscription?.addons?.find(addon => addon.name === "Event Insight");
      return eventInsightAddon?.enabled === true;
    };

    // Helper function to check if an OU is selected (all its non-onboarded accounts are selected)
    const isOUSelected = (ou) => {
      const selectableAccounts = ou.accounts.filter(acc => !isAccountDisabledInEventInsight(acc));
      return selectableAccounts.length > 0 && selectableAccounts.every(acc => isAccountSelected(acc.accountId));
    };

    // Helper function to check if root is selected (all non-onboarded accounts are selected)
    const isRootSelected = () => {
      const allSelectableAccounts = organizationalUnits.flatMap(ou => 
        ou.accounts.filter(acc => !isAccountDisabledInEventInsight(acc))
      );
      return allSelectableAccounts.length > 0 && allSelectableAccounts.every(acc => isAccountSelected(acc.accountId));
    };

    // Helper function to check if an OU has any selectable accounts
    const hasSelectableAccounts = (accounts) => {
      return accounts.some((acc) => !isAccountDisabledInEventInsight(acc));
    };

    // Helper function to check if root has any selectable accounts
    const hasAnySelectableAccounts = () => {
      return organizationalUnits.some((ou) =>
        ou.accounts.some((acc) => !isAccountDisabledInEventInsight(acc))
      );
    };

    const handleToggle = (nodeId, type, accounts = []) => {
      dispatch(resetGenerateYaml());

      if (type === "account") {
        setSelectedAccounts((prev) =>
          prev.includes(nodeId)
            ? prev.filter((id) => id !== nodeId)
            : [...prev, nodeId]
        );
      } else if (type === "ou") {
        const selectableAccountIds = accounts
          .filter(acc => !isAccountDisabledInEventInsight(acc))
          .map((acc) => acc.accountId);
        
        const allSelected = selectableAccountIds.every((id) =>
          selectedAccounts.includes(id)
        );
        
        if (allSelected) {
          // Deselect all accounts in this OU
          setSelectedAccounts((prev) =>
            prev.filter((id) => !selectableAccountIds.includes(id))
          );
        } else {
          // Select all non-onboarded accounts in this OU
          setSelectedAccounts((prev) => {
            const newSelected = [...prev];
            selectableAccountIds.forEach((id) => {
              if (!newSelected.includes(id)) {
                newSelected.push(id);
              }
            });
            return newSelected;
          });
        }
      } else if (type === "root") {
        const allSelectableAccountIds = organizationalUnits
          .flatMap((ou) => ou.accounts)
          .filter(acc => !isAccountDisabledInEventInsight(acc))
          .map((acc) => acc.accountId);
        
        const allSelected = allSelectableAccountIds.every((id) =>
          selectedAccounts.includes(id)
        );
        
        if (allSelected) {
          // Deselect all accounts
          setSelectedAccounts((prev) =>
            prev.filter((id) => !allSelectableAccountIds.includes(id))
          );
        } else {
          // Select all non-onboarded accounts
          setSelectedAccounts((prev) => {
            const newSelected = [...prev];
            allSelectableAccountIds.forEach((id) => {
              if (!newSelected.includes(id)) {
                newSelected.push(id);
              }
            });
            return newSelected;
          });
        }
      }
    };

    const handleRegionChange = (e) => {
      setSelectedRegion(e.target.value);
      dispatch(resetGenerateYaml());
    };

    const buildPayload = () => {
      const deploymentTargetsMap = {};

      organizationalUnits.forEach((ou) => {
        // Get specifically selected accounts in this OU
        const selectedAccountsInOU = ou.accounts
          .filter((acc) => !isAccountDisabledInEventInsight(acc) && isAccountSelected(acc.accountId))
          .map((acc) => acc.accountId);

        if (selectedAccountsInOU.length > 0) {
          deploymentTargetsMap[ou.organizationalUnitId] = selectedAccountsInOU;
        }
      });

      const deploymentTargets = Object.entries(deploymentTargetsMap).map(
        ([organizationUnitId, awsAccountIds]) => ({
          organizationUnitId,
          awsAccountIds,
          region: selectedRegion,
        })
      );

      return { deploymentTargets };
    };

    const handleGenerateYaml = () => {
      const payload = buildPayload();

      if (payload.deploymentTargets.length === 0) {
        alert("Please select at least one AWS account.");
        return;
      }

      dispatch({
        type: "organizationalUnits/generateYaml",
        payload,
      });
    };

    const handleClearSelection = () => {
      setSelectedAccounts([]);
      setSelectedRegion("");
      stopStackStatusPolling();
      setIsCheckingStatus(false);
      setStatusMessage("");
      setIsCheckingAccountStatus(false);
      setHasCheckedAccountStatus(false);
      setIsLaunched(false);
      setShowEventInsightPrompt(false);
      setEnableEventInsight(null);
      setOriginallySelectedAccounts([]);
      dispatch(resetGenerateYaml());
      // Reset stack and account activity states
      dispatch({
        type: "organizationalUnits/stackStatusResponseMethod",
        payload: null,
      });
      dispatch({
        type: "organizationalUnits/accountActivitySuccessMethod",
        payload: null,
      });
      dispatch({
        type: "organizationalUnits/stackStatusErrorMethod",
        payload: null,
      });
      dispatch({
        type: "organizationalUnits/accountActivityErrorMethod",
        payload: null,
      });
    };

    const handleLaunchYaml = () => {
      if (generateYamlResponse?.yamlPresignedUrl) {
        const encodedURL = encodeURIComponent(
          generateYamlResponse.yamlPresignedUrl
        );
        const cfURL = `https://console.aws.amazon.com/cloudformation/home#/stacks/create/review?templateURL=${encodedURL}`;
        window.open(cfURL, "_blank");
        setIsLaunched(true);
      }
    };

    const handleCheckStatus = () => {
      setIsConfirmationOpen(true);
    };

    const handleConfirmationResponse = (confirmed) => {
      setIsConfirmationOpen(false);
      if (confirmed) {
        // Start checking stack status
        setIsCheckingStatus(true);
        setStatusMessage("In Progress");
        startStackStatusPolling();
      }
    };

    const startStackStatusPolling = () => {
      // Get only selected non-onboarded account IDs
      const selectedAccountIds = organizationalUnits
        .flatMap((ou) => ou.accounts)
        .filter((acc) => !isAccountDisabledInEventInsight(acc) && isAccountSelected(acc.accountId))
        .map((acc) => acc.accountId)
        .join(",");

      const payload = {
        masterAccountNumber: masterAccountDetails.accountNumber,
        stackName: "COMPLETE",
        externalId: masterAccountDetails.externalId,
        roleSessionName: masterAccountDetails.roleSessionName,
        memberAccountIds: selectedAccountIds,
      };

      // Initial check
      dispatch(checkStackStatusMethod(payload));

      // Set up polling interval (check every 15 seconds)
      const interval = setInterval(() => {
        dispatch(checkStackStatusMethod(payload));
      }, 15000);

      setPollingInterval(interval);
    };

    const stopStackStatusPolling = () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    };

    const handleAccountStatus = () => {
      // Get only selected non-onboarded account IDs
      const selectedAccountIds = organizationalUnits
        .flatMap((ou) => ou.accounts)
        .filter((acc) => !isAccountDisabledInEventInsight(acc) && isAccountSelected(acc.accountId))
        .map((acc) => acc.accountId)
        .join(",");

      const payload = {
        memberAccountIds: selectedAccountIds,
      };

      // One-time check
      dispatch(checkAccountActivityMethod(payload));
      setIsCheckingAccountStatus(true);
      setHasCheckedAccountStatus(true);
    };

    // Effect to handle stack status response
    useEffect(() => {
      console.log(
        "🚀 ~ OrganizationalTreeSelector ~ stackStatusError:",
        stackStatusError
      );

      if (stackStatusSuccessResponse) {
        // Check if we received the expected message
        if (stackStatusSuccessResponse === "COMPLETE") {
          stopStackStatusPolling();
          setIsCheckingStatus(false);
          setStatusMessage("Completed");
        } else {
          // For any other status, show "In Progress"
          setStatusMessage("In Progress");
        }
      }

      if (stackStatusError) {
        stopStackStatusPolling();
        setIsCheckingStatus(false);
        // Extract the error message from the error object
        const errorMessage =
          typeof stackStatusError === "string"
            ? stackStatusError
            : stackStatusError.message || JSON.stringify(stackStatusError);
        setStatusMessage(errorMessage);
      }
    }, [stackStatusSuccessResponse, stackStatusError]);

    // Effect to handle account status response
    useEffect(() => {
      if (accountActivitySuccessResponse || accountActivityError) {
        setIsCheckingAccountStatus(false);
      }
    }, [accountActivitySuccessResponse, accountActivityError]);

    // Add useEffect to handle cleanup when component unmounts
    useEffect(() => {
      // This will run when the component unmounts
      return () => {
        stopStackStatusPolling();
      };
    }, []);

    // Create a wrapper for the closeModal function that also stops polling
    const handleModalClose = () => {
      stopStackStatusPolling();
      if (closeModal) {
        closeModal();
      }
    };

    // Expose the handleModalClose function to parent components
    React.useImperativeHandle(ref, () => ({
      stopPolling: stopStackStatusPolling,
      handleModalClose,
    }));

    const renderAccounts = (accounts, parentOUId) =>
      accounts.map((acc) => {
        let disabled = isAccountDisabledInEventInsight(acc);
        let checked = isAccountSelected(acc.accountId);
        const accountId = acc.accountId;
        
        // Get additional info for Event Insight mode
        let disabledReason = "";
        if (showEventInsightPrompt && disabled) {
          console.log("🔍 Render - Checking account:", acc.accountName, "accountId:", acc.accountId, "accountNumber:", acc.accountNumber);
          const matchingOrgAccount = organizations?.find(org => 
            org.accountNumber === acc.accountId || 
            org.accountNumber === acc.accountNumber ||
            org.accountNumber === acc.accountId
          );
          console.log("🔍 Render - Matching org account:", matchingOrgAccount);
          if (!matchingOrgAccount) {
            disabledReason = "Not onboarded in this Organization";
            console.log("🔍 Render - No match found, setting disabled reason");
          } else {
            const wasOriginallySelected = originallySelectedAccounts.includes(acc.accountId);
            const eventInsightAddon = matchingOrgAccount.subscription?.addons?.find(addon => addon.name === "Event Insight");
            const isEventInsightEnabled = eventInsightAddon?.enabled === true;
            
            if (!wasOriginallySelected && isEventInsightEnabled) {
              disabledReason = "Event Insight already enabled";
            } else if (acc.accountId === masterAccountDetails.accountNumber) {
              disabledReason = "Master account";
            }
          }
        }
        
        return (
          <TreeItem
            key={accountId}
            nodeId={accountId}
            label={
              <Box display="flex" alignItems="center">
                <Checkbox
                  checked={checked}
                  onChange={() => handleToggle(accountId, "account")}
                  disabled={disabled}
                  size="small"
                />
                <Typography fontSize="0.85rem">
                  {acc.accountName} |{" "}
                  {acc.accountNumber || acc.accountId || "N/A"}
                </Typography>
                {acc.onboarded && (
                  <Chip
                    label="Onboarded"
                    size="small"
                    color="success"
                    style={{ marginLeft: 8, height: 20, fontSize: "0.7rem" }}
                  />
                )}
                {showEventInsightPrompt && getEventInsightStatus(acc.accountId) && (
                  <Chip
                    label="Event Insight Enabled"
                    size="small"
                    color="primary"
                    style={{ marginLeft: 8, height: 20, fontSize: "0.7rem" }}
                  />
                )}
                {disabled && disabledReason && (
                  <Typography 
                    fontSize="0.7rem" 
                    color="text.secondary" 
                    style={{ marginLeft: 8, fontStyle: "italic" }}
                  >
                    ({disabledReason})
                  </Typography>
                )}
              </Box>
            }
          />
        );
      });

    const handlePreviewYaml = () => {
      if (generateYamlResponse?.yamlPresignedUrl) {
        setIsYamlPreviewOpen(true);
      }
    };

    const handleCloseYamlPreview = () => {
      setIsYamlPreviewOpen(false);
    };

    const handleEventInsightResponse = (response) => {
      setEnableEventInsight(response);
      if (response === false) {
        // User selected No, close the modal
        if (typeof onAccountsAdded === "function") onAccountsAdded();
      } else {
        // User selected Yes, store originally selected accounts and show Event Insight section
        setOriginallySelectedAccounts([...selectedAccounts]);
        setShowEventInsightPrompt(true);
      }
    };

    // Function to clear Event Insight related states
    const clearEventInsightState = () => {
      setShowEventInsightPrompt(false);
      setEnableEventInsight(null);
      setOriginallySelectedAccounts([]);
    };

    // Wrapper function to handle account addition with Event Insight cleanup
    const handleAccountsAddedWithCleanup = () => {
      clearEventInsightState();
      // Clear organizational units state
      dispatch(clearOrganizationalUnits());
      // Call the parent's closeModal function to properly reset the entire modal
      if (typeof closeModal === "function") {
        closeModal();
      }
      // Also call onAccountsAdded to refresh the datagrid
      if (typeof onAccountsAdded === "function") {
        onAccountsAdded();
      }
    };

    return (
      <Box
        mt={4}
        sx={{ display: "flex", flexDirection: "column", width: "100%" }}
      >
        <Box
          sx={{
            flex: 1,
            width: "100%",
          }}
        >
          <Typography variant="h6" fontWeight={600} color="text.primary" mb={1}>
            AWS Organization
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={3}>
            Select OUs and/or AWS accounts to include in the YAML deployment.
          </Typography>

          <TreeView
            defaultCollapseIcon={<ExpandMoreIcon />}
            defaultExpandIcon={<ChevronRightIcon />}
          >
            <TreeItem
              nodeId="root"
              label={
                <Box display="flex" alignItems="center">
                  <Checkbox
                    checked={isRootSelected()}
                    onChange={() => handleToggle("root", "root")}
                    disabled={!hasAnySelectableAccounts()}
                  />
                  <Typography fontWeight={500}>Root</Typography>
                </Box>
              }
            >
              {organizationalUnits.map((ou) => (
                <TreeItem
                  key={ou.organizationalUnitId}
                  nodeId={ou.organizationalUnitId}
                  label={
                    <Box display="flex" alignItems="center">
                      <Checkbox
                        checked={isOUSelected(ou)}
                        onChange={() =>
                          handleToggle(
                            ou.organizationalUnitId,
                            "ou",
                            ou.accounts
                          )
                        }
                        disabled={!hasSelectableAccounts(ou.accounts)}
                      />
                      <Typography fontWeight={500}>
                        {ou.organizationalUnitName}
                      </Typography>
                    </Box>
                  }
                >
                  {renderAccounts(ou.accounts, ou.organizationalUnitId)}
                </TreeItem>
              ))}
            </TreeItem>
          </TreeView>

          <Box mt={4} mb={3}>
            <Typography
              variant="subtitle1"
              fontWeight={600}
              gutterBottom
              color="text.primary"
            >
              Select AWS Region
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={selectedRegion}
                onChange={handleRegionChange}
                displayEmpty
                renderValue={(selected) =>
                  selected ? (
                    awsRegions.list().find((r) => r.code === selected)?.name +
                    " — " +
                    selected
                  ) : (
                    <em>Choose a region</em>
                  )
                }
              >
                <MenuItem disabled value="">
                  <em>Choose a region</em>
                </MenuItem>
                {awsRegions.list().map((region) => (
                  <MenuItem key={region.code} value={region.code}>
                    {region.name} — {region.code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box
            mt={3}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {accountActivitySuccessResponse !== "COMPLETE" && (
              <Button
                variant="outlined"
                style={{
                  color: "#F98125",
                  borderColor: "#F98125",
                  borderRadius: "6px",
                  textTransform: "none",
                  fontWeight: 500,
                  minWidth: "150px",
                }}
                onClick={handleClearSelection}
                sx={{
                  "&:hover": {
                    backgroundColor: "#F98125",
                    color: "#fff",
                  },
                }}
              >
                Clear Selection
              </Button>
            )}

            {isGeneratingYaml ? (
              <Typography variant="body2" color="textSecondary">
                Generating YAML...
              </Typography>
            ) : stackStatusError ? (
              <Button
                variant="contained"
                disabled
                style={{
                  backgroundColor: "#d32f2f",
                  color: "#fff",
                  minWidth: "150px",
                }}
              >
                {stackStatusError}
              </Button>
            ) : stackStatusSuccessResponse === "COMPLETE" ? (
              <Box display="flex" alignItems="center" gap={2}>
                {!isCheckingAccountStatus && !hasCheckedAccountStatus && (
                  <Typography variant="body2" color="success.main">
                    Stack completed successfully!
                  </Typography>
                )}
                {accountActivitySuccessResponse === "COMPLETE" ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    sx={{ width: "100%", marginRight: 0 }}
                  >
                    <Typography variant="body2" color="success.main">
                      Accounts are added and active
                    </Typography>

                    {!showEventInsightPrompt ? (
                      <Box>
                        <Typography
                          variant="body1"
                          color="textSecondary"
                          mb={3}
                          sx={{ lineHeight: 1.6 }}
                        >
                          Do you want to enable cloud events for your master
                          account? This will create an organizational trail in
                          the master account for the selected member accounts.
                        </Typography>
                        <Box display="flex" gap={2} justifyContent="flex-end">
                          <Button
                            variant="outlined"
                            onClick={() => handleEventInsightResponse(false)}
                            style={{
                              color: "#F98125",
                              borderColor: "#F98125",
                              minWidth: "80px",
                              borderRadius: "6px",
                              textTransform: "none",
                              fontWeight: 500,
                            }}
                            sx={{
                              "&:hover": {
                                backgroundColor: "#F98125",
                                color: "#fff",
                              },
                            }}
                          >
                            No
                          </Button>
                          <Button
                            variant="contained"
                            onClick={() => handleEventInsightResponse(true)}
                            style={{
                              backgroundColor: "#F98125",
                              color: "#fff",
                              minWidth: "80px",
                              borderRadius: "6px",
                              textTransform: "none",
                              fontWeight: 500,
                            }}
                            sx={{
                              "&:hover": {
                                backgroundColor: "#f37200",
                              },
                            }}
                          >
                            Yes
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <EventInsightSection
                        organizationalUnits={organizationalUnits}
                        masterAccountDetails={masterAccountDetails}
                        selectedNodes={selectedAccounts}
                        onAccountsAdded={handleAccountsAddedWithCleanup}
                        onCleanup={handleAccountsAddedWithCleanup}
                        onBack={() => {
                          setShowEventInsightPrompt(false);
                        }}
                      />
                    )}
                  </Box>
                ) : accountActivityError ? (
                  <Box display="flex" alignItems="center" gap={3}>
                    <Typography variant="body2" color="error">
                      Process failed! Please contact Administrator!
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={handleAccountStatus}
                      style={{
                        color: "#1976d2",
                        borderColor: "#1976d2",
                        minWidth: "150px",
                      }}
                    >
                      Try Again!
                    </Button>
                  </Box>
                ) : (
                  <Box display="flex" alignItems="center" gap={3}>
                    <Button
                      variant="outlined"
                      onClick={handleAccountStatus}
                      disabled={isCheckingAccountStatus}
                      style={{
                        color: "#1976d2",
                        borderColor: "#1976d2",
                        minWidth: "200px",
                      }}
                      sx={{
                        "&:hover": {
                          backgroundColor: isCheckingAccountStatus ? "transparent" : "#1976d2",
                          color: isCheckingAccountStatus ? "#1976d2" : "#fff",
                        },
                        "&:disabled": {
                          color: "#1976d2",
                          borderColor: "#1976d2",
                          opacity: 0.7,
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      {isCheckingAccountStatus ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={16} style={{ color: "#1976d2" }} />
                          <span>Account Verification in Progress...</span>
                        </Box>
                      ) : hasCheckedAccountStatus ? (
                        "Check Again!"
                      ) : (
                        "Check Account Status"
                      )}
                    </Button>
                  </Box>
                )}
              </Box>
            ) : isCheckingStatus ? (
              <Button
                variant="contained"
                disabled
                style={{
                  backgroundColor: "#757575",
                  color: "#fff",
                  minWidth: "150px",
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={20} color="inherit" />
                  <span>In Progress</span>
                </Box>
              </Button>
            ) : generateYamlResponse?.yamlPresignedUrl &&
              selectedAccounts.length > 0 &&
              selectedRegion ? (
              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  onClick={handlePreviewYaml}
                  style={{
                    color: "#2e7d32",
                    borderColor: "#2e7d32",
                    minWidth: "150px",
                  }}
                >
                  Review YAML
                </Button>
                {!isLaunched ? (
                  <Button
                    variant="contained"
                    onClick={handleLaunchYaml}
                    style={{
                      backgroundColor: "#2e7d32",
                      color: "#fff",
                      minWidth: "150px",
                    }}
                  >
                    Launch YAML
                  </Button>
                ) : (
                  <Box display="flex" gap={2}>
                    <Button
                      variant="outlined"
                      disabled
                      style={{
                        color: "#2e7d32",
                        borderColor: "#2e7d32",
                        minWidth: "150px",
                      }}
                    >
                      Launched
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleCheckStatus}
                      style={{
                        backgroundColor: "#2e7d32",
                        color: "#fff",
                        minWidth: "150px",
                      }}
                    >
                      Check Status
                    </Button>
                  </Box>
                )}
              </Box>
            ) : (
              <Button
                variant="contained"
                onClick={handleGenerateYaml}
                disabled={!selectedRegion || selectedAccounts.length === 0}
                sx={{
                  backgroundColor: "#F98125",
                  color: "#fff",
                  opacity:
                    !selectedRegion || selectedAccounts.length === 0 ? 0.5 : 1,
                  pointerEvents:
                    !selectedRegion || selectedAccounts.length === 0
                      ? "none"
                      : "auto",
                  transition: "opacity 0.3s",
                  borderRadius: "6px",
                  textTransform: "none",
                  fontWeight: 500,
                  minWidth: "150px",
                  "&:hover": {
                    backgroundColor: "#f37200",
                  },
                  "&:disabled": {
                    backgroundColor: "#ccc",
                    color: "#666",
                  },
                }}
                title={
                  !selectedRegion || selectedAccounts.length === 0
                    ? "Please select at least one account and a region"
                    : ""
                }
              >
                Generate YAML
              </Button>
            )}
          </Box>
        </Box>

        {/* Display Generate YAML Error */}
        {generateYamlError && (
          <Box mt={2}>
            <Alert 
              severity="error" 
              onClose={() => {
                dispatch({
                  type: "organizationalUnits/resetGenerateYaml",
                });
              }}
            >
              {(() => {
                if (typeof generateYamlError === 'string') {
                  return generateYamlError;
                }
                if (generateYamlError?.message) {
                  return generateYamlError.message;
                }
                if (generateYamlError?.data?.message) {
                  return generateYamlError.data.message;
                }
                if (typeof generateYamlError === 'object') {
                  return JSON.stringify(generateYamlError);
                }
                return 'Failed to generate YAML. Please try again.';
              })()}
            </Alert>
          </Box>
        )}

        <Dialog
          open={isConfirmationOpen}
          onClose={() => handleConfirmationResponse(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            style: { overflow: "hidden" },
          }}
        >
          <DialogTitle style={{ backgroundColor: "#F98125", padding: "16px" }}>
            <Typography color="#FFFFFF" variant="h6">
              Confirm Stack Creation
            </Typography>
          </DialogTitle>
          <DialogContent style={{ marginTop: 20, padding: "20px" }}>
            <Typography>
              Can you confirm if the stack has been created in your AWS
              environment?
            </Typography>
          </DialogContent>
          <DialogActions style={{ padding: "20px" }}>
            <Button
              variant="outlined"
              onClick={() => handleConfirmationResponse(false)}
              style={{ minWidth: 100 }}
            >
              No
            </Button>
            <Button
              variant="contained"
              onClick={() => handleConfirmationResponse(true)}
              style={{
                backgroundColor: "#F98125",
                color: "#fff",
                minWidth: 100,
              }}
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={isYamlPreviewOpen}
          onClose={handleCloseYamlPreview}
          maxWidth="md"
          fullWidth
          TransitionComponent={Slide}
          TransitionProps={{ direction: "left" }}
          PaperProps={{
            style: {
              height: "100%",
              margin: 0,
              position: "fixed",
              right: 0,
              borderRadius: 0,
              maxWidth: "50%",
              backgroundColor: "#fff",
              zIndex: 1300,
            },
          }}
        >
          <DialogTitle
            sx={{
              backgroundColor: "#F98125",
              color: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "16px 24px",
              margin: 0,
              borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
              position: "relative",
              zIndex: 1301,
            }}
          >
            <Typography color="#FFFFFF" variant="h6">
              YAML Preview
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleCloseYamlPreview}
              aria-label="close"
              sx={{
                marginRight: -1,
                zIndex: 1302,
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{
              padding: 0,
              height: "calc(100% - 64px)",
              backgroundColor: "#ffffff",
              position: "relative",
              zIndex: 1300,
              colorScheme: "light",
            }}
          >
            <iframe
              src={generateYamlResponse?.yamlPresignedUrl}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                backgroundColor: "#ffffff",
                colorScheme: "light",
              }}
              title="YAML Preview"
            />
          </DialogContent>
        </Dialog>
      </Box>
    );
  }
);

export default OrganizationalTreeSelector; 